import { Link, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MenuItem } from "@mui/material";
import { Badge } from "react-bootstrap";
import Sidebar from "../../components/sidebar/Sidebar";
import LoadingSpinner from "../../components/LoadingSpinner";
import "../accounts/view/ViewAccountPage.css";
import { MUI_TABLE } from '../../utils/muiTableConfig';
import { Money, Username } from '../../utils/privacyMask';


export default function TransactionsPage() {
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [accountsMap, setAccountsMap]   = useState({});
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [txRes, accRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/transaction`, { headers }),
          axios.get(`${process.env.REACT_APP_API_URL}/account`, { headers }),
        ]);
        setTransactions(txRes.data);
        const map = {};
        accRes.data.forEach(a => { map[a._id] = a.username; });
        setAccountsMap(map);
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch transactions");
      }
    };
    fetchData();
  }, []);

  const columns = useMemo(() => [
    {
      header: 'Account', accessorKey: 'accountID',
      Cell: ({ cell }) => <span style={{ fontWeight: 600 }}><Username value={accountsMap[cell.getValue()] || cell.getValue() || '—'} /></span>,
    },
    {
      header: 'Type', accessorFn: r => r.type === 'expense' ? (r.customCategory || r.category || 'expense') : 'sale', size: 130,
      Cell: ({ row }) => {
        const isExpense = row.original.type === 'expense';
        const label = isExpense ? (row.original.customCategory || row.original.category || 'expense') : 'Sale';
        return (
          <Badge bg="" style={{
            background: isExpense ? 'rgba(239,68,68,0.14)' : 'rgba(16,185,129,0.14)',
            color: isExpense ? '#f87171' : '#34d399',
            border: `1px solid ${isExpense ? 'rgba(239,68,68,0.35)' : 'rgba(16,185,129,0.35)'}`,
            fontSize: '0.72rem', fontWeight: 600, textTransform: 'capitalize',
          }}>
            {label}
          </Badge>
        );
      },
    },
    {
      header: 'Date', accessorKey: 'transactionDate', size: 110,
      Cell: ({ cell }) => new Date(Number(cell.getValue())).toLocaleDateString(),
    },
    {
      header: 'Revenue', accessorKey: 'revenue', size: 100,
      Cell: ({ cell }) => <span style={{ color: '#34d399', fontWeight: 600 }}><Money value={cell.getValue()} /></span>,
    },
    {
      header: 'Profit', accessorKey: 'profit', size: 100,
      Cell: ({ cell }) => <span style={{ color: '#38bdf8' }}><Money value={cell.getValue()} /></span>,
    },
    {
      header: 'Method', accessorKey: 'paymentMethod', size: 100,
      Cell: ({ cell }) => {
        const m = cell.getValue();
        return m
          ? <span style={{ textTransform: 'capitalize', color: '#94a3b8' }}>{m}</span>
          : <span style={{ color: '#334155' }}>—</span>;
      },
    },
    {
      header: 'Notes', accessorKey: 'notes',
      Cell: ({ cell }) => <span style={{ color: '#64748b' }}>{cell.getValue() || '—'}</span>,
    },
  ], [accountsMap]);

  const table = useMaterialReactTable({
    columns,
    data: transactions ?? [],
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: false,
    enableGlobalFilter: true,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: false,
      columnPinning: { left: ['mrt-row-expand', 'mrt-row-select'], right: ['mrt-row-actions'] },
    },
    renderRowActionMenuItems: ({ row }) => [
      <MenuItem key="edit"   onClick={() => navigate(`/transaction/edit/${row.original._id}`)} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-pencil-square" style={{ fontSize: '0.9rem', color: '#64748b' }} />Edit
      </MenuItem>,
      <MenuItem key="view"   onClick={() => navigate(`/transaction/view/${row.original._id}`)} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-eye" style={{ fontSize: '0.9rem', color: '#64748b' }} />View
      </MenuItem>,
      <MenuItem key="delete" onClick={() => {}} sx={{ m: 0, gap: 1, color: '#f87171' }}>
        <i className="bi bi-trash" style={{ fontSize: '0.9rem', color: '#f87171' }} />Delete
      </MenuItem>,
    ],
    ...MUI_TABLE,
  });

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Transactions" />

      <div className="view-page">
        {error && (
          <div style={{ padding: '12px 16px', marginBottom: 20, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        )}

        <div className="view-panel">
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-receipt" style={{ color: '#06b6d4' }} />
              All Transactions
            </h6>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="view-panel-count">{transactions.length}</span>
              <Link to="/transaction/expense/create" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                borderRadius: 8, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)',
                color: '#f87171', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none',
                transition: 'background 0.18s, border-color 0.18s',
              }}>
                <i className="bi bi-plus-lg" /> New Expense
              </Link>
              <Link to="/transaction/create" style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                borderRadius: 8, border: '1px solid rgba(6,182,212,0.3)', background: 'rgba(6,182,212,0.08)',
                color: '#22d3ee', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none',
                transition: 'background 0.18s, border-color 0.18s',
              }}>
                <i className="bi bi-plus-lg" /> New Transaction
              </Link>
            </div>
          </div>
          <MaterialReactTable table={table} />
        </div>
      </div>
    </>
  );
}
